import { tz } from '@date-fns/tz';
import { openingAndClosing } from '@iheartjane/menu';
import type { TimeZone, WorkingHour } from '@iheartjane/types';
import { timezone as parseApiTimezones } from '@iheartjane/utils';
import { getDay, subDays, subSeconds } from 'date-fns';

import { timeIsBetween } from './timeIsBetween';

export const getDates = (
  schedule: WorkingHour | undefined,
  storeTimezone: TimeZone,
  yesterday = false
): [Date, Date] | [null, null] => {
  const from = schedule?.period?.from;
  const to = schedule?.period?.to;

  if (!from || !to) return [null, null];

  const now = new Date();
  const scheduleDate = yesterday
    ? subDays(now, 1, { in: tz(storeTimezone) })
    : now;

  return openingAndClosing({ from, to }, storeTimezone, scheduleDate);
};

interface StoreWithScheduleInfo {
  allow_future_day_ordering?: boolean;
  allow_off_hours_ordering?: boolean;
  curbside_hours?: readonly WorkingHour[] | null;
  curbside_last_call_interval: number;
  delivery?: boolean | null;
  delivery_hours?: readonly WorkingHour[] | null;
  delivery_last_call_interval: number;
  last_call_interval: number;
  pickup?: boolean | null;
  pickup_hours?: readonly WorkingHour[] | null;
  time_zone_identifier: TimeZone;
}

export const isAcceptingOrders = (
  store: StoreWithScheduleInfo,
  reservationMode: 'delivery' | 'pickup' | 'curbside'
): boolean => {
  // Ensure delivery or pickup are enabled
  if (!store[reservationMode === 'delivery' ? 'delivery' : 'pickup'])
    return false;

  // If the store allows future day/off hours ordering, we don't need to parse the schedule,
  // we just make sure a schedule exists for this reservation mode.
  if (
    (store.allow_off_hours_ordering || store.allow_future_day_ordering) &&
    !!store[`${reservationMode}_hours`]
  ) {
    return true;
  }

  const storeTimezone = parseApiTimezones(store.time_zone_identifier);
  const inStoreTz = { in: tz(storeTimezone) };
  const now = new Date();
  const today = getDay(now, inStoreTz);
  const yesterday = getDay(subDays(now, 1, inStoreTz), inStoreTz);

  const scheduleToday = store[`${reservationMode}_hours`]?.[today];
  const scheduleYesterday = store[`${reservationMode}_hours`]?.[yesterday];

  const [todayOpen, todayClose] = getDates(scheduleToday, storeTimezone);
  const [yesterdayOpen, yesterdayClose] = getDates(
    scheduleYesterday,
    storeTimezone,
    true
  );

  const openToday = !!todayOpen && !!todayClose;
  const openYesterday = !!yesterdayOpen && !!yesterdayClose;

  const lastCallInSeconds =
    reservationMode === 'pickup'
      ? store.last_call_interval
      : store[`${reservationMode}_last_call_interval`];

  if (
    openToday &&
    timeIsBetween(
      now,
      todayOpen,
      subSeconds(todayClose, lastCallInSeconds || 0)
    )
  ) {
    return true;
  }

  // In some situations, we need to also check yesterday's schedule.
  // ex. A store is open Tue 8am-2am and it is currently 1am on Wed.
  if (
    openYesterday &&
    timeIsBetween(
      now,
      yesterdayOpen,
      subSeconds(yesterdayClose, lastCallInSeconds || 0)
    )
  ) {
    return true;
  }

  return false;
};

export const isAcceptingOrdersForStore = (
  store: StoreWithScheduleInfo
): boolean => {
  const delivery = isAcceptingOrders(store, 'delivery');
  const pickup = isAcceptingOrders(store, 'pickup');

  return delivery || pickup;
};
